import gql from 'graphql-tag'
import Model, { executeMutation } from 'src/models/model'

/**
 * @param {import('@quasar/app-webpack').BootFileParams} params
 */
export default async ({ app }) => {
  if (!app.config.globalProperties.$remoteCommands) return

  const remoteCommands = app.config.globalProperties.$remoteCommands
  const paymentConnectorHandler = {}
  const PAYMENT_CONNECTOR_REST_RESPONSE = 'payment-connector-rest-response'
  const PERFORM_PAYMENT_CONNECTOR_MAINTENANCE_ACTION = 'perform_payment_connector_maintenance_action'
  const UPDATE_PAYMENT_CONNECTOR_STATUS = 'update-payment-connector-status'
  const PRINT_CACHED_MERCHANT_RECEIPT = 'print_cached_merchant_receipt'
  const PRINT_CACHED_CUSTOMER_RECEIPT = 'print_cached_customer_receipt'

  const fetchFunction = window.localhostFetch

  const paymentConnectorQueue = []
  let jobProcessing = false

  // This function manages the queue of payment connector requests
  // It will process the next job in the queue once the current job is finished
  // This is because the payment connector can only handle one request at a time
  function addToQueue (job = null) {
    // If we are adding a job to the queue, add it
    // If we are not adding a job to the queue, check if there are any jobs to process
    if (job) {
      paymentConnectorQueue.push(job)
    }

    // If we have jobs to process and the payment connector is not currently processing a job, process the next job
    // Otherwise when the request is finished, the next job will be processed recursively
    if (paymentConnectorQueue.length > 0 && !jobProcessing) {
      jobProcessing = true
      // Process the next job in the queue, jobs are always functions
      paymentConnectorQueue.pop()().then(res => {
        if (paymentConnectorQueue.length > 0) {
          jobProcessing = false
          // Process the next job in the queue recursively
          addToQueue()
        } else {
          // If there are no more jobs in the queue, set jobProcessing to false
          // Any subsequent jobs will be processed when they are added to the queue
          jobProcessing = false
        }
      })
    }
  }

  remoteCommands.handlePaymentUpdate = async function (data) {
    paymentConnectorHandler.updated(data.payment_id, data.refetch_order)
  }

  paymentConnectorHandler.requestCancel = async function () {
    if (paymentConnectorHandler.cancel) {
      return await paymentConnectorHandler.cancel()
    }
    return true
  }

  paymentConnectorHandler.printCachedReceipt = async function (registerClient, paymentId, customer = false) {
    paymentConnectorHandler.performMaintenanceAction(registerClient, customer ? PRINT_CACHED_CUSTOMER_RECEIPT : PRINT_CACHED_MERCHANT_RECEIPT, { payment_id: paymentId })
  }

  paymentConnectorHandler.sendPaymentStatusRequest = async function (paymentId) {
    // Wait for local request to finish
    if (paymentConnectorQueue.length > 0 || jobProcessing) return

    const mutation = gql`mutation ($input: SendRemoteCommandInput!){
      sendRemoteCommand(input: $input) {
        recipientCount
        errors {
          code
          description
        }
      }
    }`

    const command = UPDATE_PAYMENT_CONNECTOR_STATUS
    const data = { payment_id: paymentId, rest_request_in_progress: paymentConnectorQueue.length > 0 || jobProcessing }
    const destination = ''

    const result = await Model.apollo.mutate({
      mutation,
      variables: {
        input: {
          remoteCommandInput: {
            command,
            data,
            destination
          }
        }
      }
    })

    return result.data.sendRemoteCommand
  }

  // This is for handling requests from the API to execute a payment connector command.
  // This is used for payment connectors that require a REST request to be made on the client network.
  // E.g. For Windcave they have an external API, so our API can process the request without the frontend.
  // For other payment providers like Evertec, there is no external API and the request must be made from the frontend.
  // This is because the payment connector is running on the client network and the request must be made from the client network.
  remoteCommands.handlePaymentRestRequest = async function (commandData) {
    const requestOptions = {
      method: commandData.method,
      headers: commandData.headers,
      body: JSON.stringify(commandData.body || {})
    }

    // Build the callback function for when the local request finishes
    // This is the function that gets executed to let our API know the result of the request
    // This essentially acts as a router between the payment connector and our API
    const executeCommand = (response) => {
      const mutation = gql`mutation ($input: SendRemoteCommandInput!){
        sendRemoteCommand(input: $input) {
          recipientCount
          errors {
            code
            description
          }
        }
      }`

      const command = PAYMENT_CONNECTOR_REST_RESPONSE
      // endpoint: the endpoint of the REST request
      // response: the response from the REST request
      // payment_id: the payment_id of the payment so the API can resolve the response to the correct payment & payment service
      const data = { endpoint: commandData.endpoint, response, payment_id: commandData.payment_id }
      const destination = ''

      Model.apollo.mutate({
        mutation,
        variables: {
          input: {
            remoteCommandInput: {
              command,
              data,
              destination
            }
          }
        }
      })
    }

    // We add the request to the queue, in the form of a function to handle the request/response
    addToQueue(async () => {
      let restRequest = null

      try {
        if (!fetchFunction) {
          executeCommand({ error: 'Communication Failed: Unable to find electron proxy server' })
          return
        }
        restRequest = await fetchFunction(commandData.url, requestOptions)
      } catch (error) {
        // If the request fails, send an error response to the API so it knows the request failed
        // This will be logged in the API and the user will be notified by returning remote command
        executeCommand({ error: 'Communication Failed: ' + error.message })
        return
      }

      let jsonResponse = null
      let errorResponse = null

      try {
        // All payment connectors currently use JSON, so we can assume the response will be JSON
        // If this changes in the future, we will need to handle different response types
        // This could be done by adding this as a interface on the payment service and adding that to the PaymentConnectorConfiguration
        jsonResponse = await restRequest?.json()
      } catch (error) {
        errorResponse = error.message
      } finally {
        // Sends the local REST payment connector response to the API
        if (errorResponse) {
          executeCommand({ error: 'Response Parsing Failed: ' + errorResponse })
        } else {
          executeCommand(jsonResponse)
        }
      }
    })
  }

  // This function is for payment connectors that require a unique transaction number for each transaction
  paymentConnectorHandler.getNextTransactionNumber = function () {
    return executeMutation('getNextTransactionNumber', {}, { required: true, transactionNumber: true })
  }

  // A maintenance action is a request to perform a maintenance action on the payment connector
  // These are usually things like "Log on", "Log off", "Settle", etc.
  // A list of supported maintenance actions is defined by the PaymentService class in the API
  // These functions are exposed in the ConnectorActionDialog.vue component
  paymentConnectorHandler.performMaintenanceAction = async function (registerClient, actionName, options = {}) {
    registerClient.action = PERFORM_PAYMENT_CONNECTOR_MAINTENANCE_ACTION
    // Some actions require options to be passed to them (e.g. "Adjusting a tip amount", requires a value)
    registerClient.paymentConnectorMaintenanceAction = { name: actionName, options }
    const result = await registerClient.save()

    registerClient.action = null

    if (result?.errors?.length) {
      throw new Error(result.errors[0].description)
    } else if (registerClient.paymentConnectorRestRequest?.no_action_required) {
      // If the maintenance action does not require a response, we are done
    } else if (!registerClient.paymentConnectorRestRequest) {
      // The result of this action should return a payment connector rest request
      // If it doesn't, something went wrong. This should never happen but we should handle it just in case
      throw new Error('No response received')
    } else {
      // Execute the resolved payment connector rest request for the maintenance action
      // Then return the result of the request to the API
      const commandData = registerClient.paymentConnectorRestRequest
      const requestOptions = {
        method: commandData.method,
        headers: commandData.headers,
        body: JSON.stringify(commandData.body || {})
      }

      let response = null

      try {
        if (!fetchFunction) {
          throw new Error('Unable to find electron proxy server')
        }
        const restRequest = await fetchFunction(commandData.url, requestOptions)
        response = await restRequest.json()
      } catch (error) {
        response = { error: 'Communication Failed: ' + error.message }
      }

      const mutation = gql`mutation ($input: SendRemoteCommandInput!){
        sendRemoteCommand(input: $input) {
          recipientCount
          errors {
            code
            description
          }
        }
      }`

      const command = PAYMENT_CONNECTOR_REST_RESPONSE
      const data = { endpoint: commandData.endpoint, response, payment_id: commandData.payment_id }
      const destination = ''

      await Model.apollo.mutate({
        mutation,
        variables: {
          input: {
            remoteCommandInput: {
              command,
              data,
              destination
            }
          }
        }
      })
    }
  }

  app.config.globalProperties.$paymentConnectorHandler = paymentConnectorHandler
}
